import React, { useState, useMemo } from 'react';
import { Button } from '../UI';
import { kuesionerData } from '../../helpers/kuesioner';
import { calculateAgeMonths } from '../../helpers/utils';
import { getAssessmentRecommendations } from '../../helpers/assessmentRecommendations';

export default function MataForm({ child, onSave }) {
    const ageInMonths = useMemo(() => calculateAgeMonths(child.dob.toDate()), [child.dob]);

    const pupilPutihQuestions = kuesionerData.pemeriksaanMata.pupilPutih.questions;
    const dayaLihatQuestions = ageInMonths >= 36 ? kuesionerData.pemeriksaanMata.dayaLihat.questions : [];

    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(false);

    const handleAnswer = (questionId, value) => {
        setAnswers(prev => ({ ...prev, [questionId]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const totalQuestions = pupilPutihQuestions.length + dayaLihatQuestions.length;
        if (Object.keys(answers).length < totalQuestions) {
            alert("Harap jawab semua pertanyaan.");
            return;
        }
        setLoading(true);

        const pupilPutihScore = pupilPutihQuestions.filter(q => answers[q.id] === 'Tidak').length;
        const dayaLihatScore = dayaLihatQuestions.filter(q => answers[q.id] === 'Tidak').length;

        const pupilPutihRecs = getAssessmentRecommendations('pupilPutih', pupilPutihScore, pupilPutihQuestions.length, ageInMonths);
        const dayaLihatRecs = getAssessmentRecommendations('dayaLihat', dayaLihatScore, dayaLihatQuestions.length, ageInMonths);

        const data = {
            pemeriksaanMata: {
                pupilPutih: {
                    status: pupilPutihRecs.status,
                    interpretasi: pupilPutihRecs.interpretation,
                    rekomendasi: pupilPutihRecs.recommendations,
                    tindakLanjut: pupilPutihRecs.followUp,
                    tingkatUrgency: pupilPutihRecs.urgency,
                    detail: `Skor Pupil Putih: ${pupilPutihQuestions.length - pupilPutihScore}/${pupilPutihQuestions.length}`,
                    answers: pupilPutihQuestions.reduce((acc, q) => ({...acc, [q.id]: answers[q.id]}), {}),
                    questions: pupilPutihQuestions
                },
                dayaLihat: ageInMonths >= 36 ? {
                    status: dayaLihatRecs.status,
                    interpretasi: dayaLihatRecs.interpretation,
                    rekomendasi: dayaLihatRecs.recommendations,
                    tindakLanjut: dayaLihatRecs.followUp,
                    tingkatUrgency: dayaLihatRecs.urgency,
                    detail: `Skor Daya Lihat: ${dayaLihatQuestions.length - dayaLihatScore}/${dayaLihatQuestions.length}`,
                    answers: dayaLihatQuestions.reduce((acc, q) => ({...acc, [q.id]: answers[q.id]}), {}),
                    questions: dayaLihatQuestions
                } : null,
            }
        };

        const overallStatus = pupilPutihRecs.status === 'Penyimpangan' || (dayaLihatRecs && dayaLihatRecs.status === 'Penyimpangan') ? 'Penyimpangan' : 'Normal';

        onSave(data, overallStatus);
    };

    const renderQuestions = (questions) => {
        return questions.map((item, index) => (
            <div key={item.id} className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
                <div className="mb-4">
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-full text-sm font-bold mr-3">
                        {index + 1}
                    </span>
                    <label className="text-gray-800 font-medium text-base leading-relaxed">{item.q}</label>
                </div>
                <div className="flex gap-3">
                    <label className={`
                        flex items-center justify-center px-6 py-3 rounded-2xl border-2 cursor-pointer
                        transition-all duration-300 transform hover:scale-105 active:scale-95
                        font-semibold text-sm shadow-md hover:shadow-lg
                        ${answers[item.id] === 'Ya' 
                            ? 'border-green-400 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 shadow-lg'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-green-200 hover:bg-green-50'
                        }
                    `}>
                        <input
                            type="radio"
                            name={`answer-${item.id}`}
                            value="Ya"
                            checked={answers[item.id] === 'Ya'}
                            onChange={() => handleAnswer(item.id, 'Ya')}
                            className="sr-only"
                            required
                        />
                        <span className="flex items-center space-x-2">
                            <span className="text-lg">✅</span>
                            <span>Ya</span>
                        </span>
                    </label>
                    <label className={`
                        flex items-center justify-center px-6 py-3 rounded-2xl border-2 cursor-pointer
                        transition-all duration-300 transform hover:scale-105 active:scale-95
                        font-semibold text-sm shadow-md hover:shadow-lg
                        ${answers[item.id] === 'Tidak' 
                            ? 'border-red-400 bg-gradient-to-r from-red-100 to-pink-100 text-red-700 shadow-lg'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-red-200 hover:bg-red-50'
                        }
                    `}>
                        <input
                            type="radio"
                            name={`answer-${item.id}`}
                            value="Tidak"
                            checked={answers[item.id] === 'Tidak'}
                            onChange={() => handleAnswer(item.id, 'Tidak')}
                            className="sr-only"
                            required
                        />
                        <span className="flex items-center space-x-2">
                            <span className="text-lg">❌</span>
                            <span>Tidak</span>
                        </span>
                    </label>
                </div>
            </div>
        ));
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-8">
            <div>
                <div className="bg-gradient-to-r from-teal-100 to-cyan-100 p-6 rounded-2xl border border-teal-200 mb-6">
                    <div className="flex items-center space-x-3 mb-2">
                        <span className="text-2xl">👁️</span>
                        <h3 className="font-bold text-teal-800">Pemeriksaan Mata - Pupil Putih</h3>
                    </div>
                    <p className="text-teal-700 text-sm opacity-80">Deteksi dini kelainan refraksi dan katarak kongenital.</p>
                </div>
                <div className="space-y-6">
                    {renderQuestions(pupilPutihQuestions)}
                </div>
            </div>

            {dayaLihatQuestions.length > 0 && (
                <div>
                    <div className="bg-gradient-to-r from-blue-100 to-indigo-100 p-6 rounded-2xl border border-blue-200 mb-6">
                        <div className="flex items-center space-x-3 mb-2">
                            <span className="text-2xl">👓</span>
                            <h3 className="font-bold text-blue-800">Pemeriksaan Mata - Daya Lihat</h3>
                        </div>
                        <p className="text-blue-700 text-sm opacity-80">Deteksi dini gangguan daya lihat pada anak usia 36 bulan ke atas.</p>
                    </div>
                    <div className="space-y-6">
                        {renderQuestions(dayaLihatQuestions)}
                    </div>
                </div>
            )}

            <Button type="submit" variant="primary" className="w-full mt-8" disabled={loading}>
                {loading ? '🔄 Menyimpan...' : '💾 Simpan Hasil Pemeriksaan Mata'}
            </Button>
        </form>
    );
}
